import { createRouter, createWebHistory } from 'vue-router'

import { isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'

declare module 'vue-router' {
    interface RouteMeta {
        layout?:       'Default' | 'Blank'
        requiresAuth:  boolean
        /** Roles that may open the route; absent = every signed-in tenant user (admin, staff). */
        roles?:        string[]
    }
}

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition)         return savedPosition
        if (to.hash)               return { el: to.hash }
        if (from.path === to.path) return {}
        return { top: 0, behavior: 'smooth' }
    },
    routes: [
        {
            path:     '/',
            name:     'home',
            redirect: '/board',
            meta:     { requiresAuth: true },
        },
        {
            path:      '/login',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            // A presentation server's one-click sign-in (API: POST /demo/login).
            path:      '/demo/:role',
            name:      'demo',
            component: () => import('@/views/DemoSignIn.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            path:      '/board',
            name:      'board',
            component: () => import('@/views/board/Board.vue'),
            meta:      { requiresAuth: true, roles: ['admin', 'staff'] },
        },
        {
            path:      '/bookings',
            name:      'bookings',
            component: () => import('@/views/booking/Bookings.vue'),
            meta:      { requiresAuth: true, roles: ['admin', 'staff'] },
        },
        {
            path:      '/closures',
            name:      'closures',
            component: () => import('@/views/closure/Closures.vue'),
            meta:      { requiresAuth: true, roles: ['admin', 'staff'] },
        },
        {
            path:      '/customers',
            name:      'customers',
            component: () => import('@/views/customer/Customers.vue'),
            meta:      { requiresAuth: true, roles: ['admin', 'staff'] },
        },
        {
            path:      '/customers/:id',
            name:      'customer',
            component: () => import('@/views/customer/Customer.vue'),
            meta:      { requiresAuth: true, roles: ['admin', 'staff'] },
        },
        {
            path:      '/schedule',
            name:      'schedule',
            component: () => import('@/views/schedule/Schedule.vue'),
            meta:      { requiresAuth: true, roles: ['admin', 'staff'] },
        },
        {
            path:      '/my-schedule',
            name:      'my-schedule',
            component: () => import('@/views/schedule/MySchedule.vue'),
            meta:      { requiresAuth: true, roles: ['admin', 'staff'] },
        },
        {
            path:      '/reports',
            name:      'reports',
            component: () => import('@/views/report/Reports.vue'),
            meta:      { requiresAuth: true, roles: ['admin', 'staff'] },
        },
        {
            path:      '/settings',
            name:      'settings',
            component: () => import('@/views/settings/Settings.vue'),
            meta:      { requiresAuth: true, roles: ['admin'] },
        },
        {
            path:      '/tenants',
            name:      'tenants',
            component: () => import('@/views/tenant/Tenants.vue'),
            meta:      { requiresAuth: true, roles: ['platform_admin'] },
        },
        {
            path:      '/pms-installations',
            name:      'pms-installations',
            component: () => import('@/views/tenant/PmsInstallations.vue'),
            meta:      { requiresAuth: true, roles: ['platform_admin'] },
        },
        {
            path:     '/:pathMatch(.*)*',
            redirect: '/',
            meta:     { requiresAuth: true },
        },
    ],
})

/** The first screen an account lands on: the platform admin has only the tenant list. */
const homeFor = (role: string) => role === 'platform_admin' ? { name: 'tenants' } : { name: 'board' }

router.beforeEach(async (to) => {
    const appStore = useAppStore()

    // The demo page signs in by itself, whoever was signed in before.
    if (to.name === 'demo') return true

    const loggedIn = isAuthenticated()

    if (!loggedIn) {
        if (to.meta.requiresAuth) {
            appStore.setLogout()
            return { name: 'login' }
        }

        return true
    }

    if (!appStore.sessionLoaded) {
        const { loadSession } = await import('@/libs/session')
        const ok = await loadSession()

        if (!ok) {
            appStore.setLogout()
            return to.name === 'login' ? true : { name: 'login' }
        }
    }

    if (to.name === 'login' || to.name === 'home') return homeFor(appStore.role)

    if (to.meta.roles && !to.meta.roles.includes(appStore.role)) return homeFor(appStore.role)

    return true
})

export default router
